// src/Semesters.js
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Toolbar from './Toolbar';
import './Semesters.css';
import myImage from '.';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import IconButton from '@mui/material/IconButton';
import OnboardingHint from './OnboardingHint';

const DropdownIcon = ({ size = 24, color = '#000', isOpen = false }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease-in-out'
    }}
  >
    <path
      d="M7 10l5 5 5-5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const apiUrl = process.env.REACT_APP_BACKEND_URL;

// Color palette from the UI you provided
const colorPalette = [
  '#F59E0B', // yellow
  '#10B981', // green
  '#06B6D4', // light blue
  '#EF4444', // red
  '#F97316', // orange
];

// Extended color palette for full picker
const extendedColorPalette = [
  // Row 1 - Reds
  '#FEE2E2', '#FECACA', '#FCA5A5', '#F87171', '#EF4444', '#DC2626', '#B91C1C', '#991B1B',
  // Row 2 - Oranges  
  '#FED7AA', '#FDBA74', '#FB923C', '#F97316', '#EA580C', '#C2410C', '#9A3412', '#7C2D12',
  // Row 3 - Yellows
  '#FEF3C7', '#FDE68A', '#FCD34D', '#FBBF24', '#F59E0B', '#D97706', '#B45309', '#92400E',
  // Row 4 - Greens
  '#D1FAE5', '#A7F3D0', '#6EE7B7', '#34D399', '#10B981', '#059669', '#047857', '#065F46',
  // Row 5 - Blues
  '#DBEAFE', '#BFDBFE', '#93C5FD', '#60A5FA', '#3B82F6', '#2563EB', '#1D4ED8', '#1E40AF',
  // Row 6 - Purples
  '#E9D5FF', '#DDD6FE', '#C4B5FD', '#A78BFA', '#8B5CF6', '#7C3AED', '#6D28D9', '#5B21B6',
  // Row 7 - Pinks
  '#FCE7F3', '#FBCFE8', '#F9A8D4', '#F472B6', '#EC4899', '#DB2777', '#BE185D', '#9D174D',
  // Row 8 - Grays
  '#F9FAFB', '#F3F4F6', '#E5E7EB', '#D1D5DB', '#9CA3AF', '#6B7280', '#4B5563', '#374151',
];

function Semesters() {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, semesters: initialSemesters } = location.state || {};
  const [semesters, setSemesters] = useState(
    initialSemesters ? initialSemesters.map(sem => ({ ...sem, collapsed: (initialSemesters.length > 1 ? true: false) })) : []
  );
  const [classes, setClasses] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSemesterName, setNewSemesterName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');
  const [isClassModalOpen, setIsClassModalOpen] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [selectedSemesterId, setSelectedSemesterId] = useState(null);
  const [editingClassId, setEditingClassId] = useState(null);
  const [editedClassName, setEditedClassName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [classColors, setClassColors] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [showFullColorPicker, setShowFullColorPicker] = useState(null);
  const [draggedClass, setDraggedClass] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [editingSemesterId, setEditingSemesterId] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const newSemesterBtnRef = useRef(null);

  // Load class colors from API data instead of localStorage
  useEffect(() => {
    const loadClassColors = () => {
      const colors = {};
      Object.values(classes).flat().forEach(cls => {
        if (cls.color) {
          colors[cls.id] = cls.color;
        }
      });
      setClassColors(colors);
    };
    
    loadClassColors();
  }, [classes]);

  const handleColorSelect = async (classId, color) => {
    try {
      const token = localStorage.getItem('jwtToken');
      await axios.put(`${apiUrl}/api/classes/${classId}/color`, {
        color: color
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // Update local classColors state for immediate UI feedback
      setClassColors(prev => ({
        ...prev,
        [classId]: color
      }));

      // Update classes state to ensure the data is consistent
      setClasses((prevClasses) => {
        const updatedClasses = { ...prevClasses };
        Object.keys(updatedClasses).forEach((semesterId) => {
          updatedClasses[semesterId] = updatedClasses[semesterId].map((cls) =>
            cls.id === classId ? { ...cls, color: color } : cls
          );
        });
        return updatedClasses;
      });

      setShowColorPicker(null);
      setShowFullColorPicker(null);
    } catch (error) {
      console.error('Error updating class color:', error);
    }
  };

  const getClassColor = (classId) => {
    return classColors[classId] || '#3B82F6'; // Default to blue
  };

  const handleDragStart = (e, cls, semesterId) => {
    setDraggedClass({ class: cls, semesterId: semesterId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex, semesterId) => {
    e.preventDefault();
    
    if (!draggedClass || draggedClass.semesterId !== semesterId) {
      setDraggedClass(null);
      setDragOverIndex(null);
      return;
    }

    const semesterClasses = [...classes[semesterId]];
    const draggedIndex = semesterClasses.findIndex(c => c.id === draggedClass.class.id);
    
    if (draggedIndex === targetIndex) {
      setDraggedClass(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder the classes
    const [reorderedClass] = semesterClasses.splice(draggedIndex, 1);
    semesterClasses.splice(targetIndex, 0, reorderedClass);

    // Update the state
    setClasses(prevClasses => ({
      ...prevClasses,
      [semesterId]: semesterClasses
    }));

    setDraggedClass(null);
    setDragOverIndex(null);

    // Save the new order to backend
    saveClassOrder(semesterId, semesterClasses);
  };

  const saveClassOrder = async (semesterId, orderedClasses) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const classIds = orderedClasses.map(cls => cls.id);
      
      await axios.post(`${apiUrl}/api/semesters/${semesterId}/class_order`, {
        class_order: classIds
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Error saving class order:', error);
    }
  };

  const sortClassesByOrder = (classes, classOrder) => {
    if (!classOrder || !Array.isArray(classOrder)) {
      return classes; // Return original order if no custom order exists
    }

    // Create a map for quick lookup
    const orderMap = {};
    classOrder.forEach((classId, index) => {
      orderMap[classId] = index;
    });

    // Sort classes according to the order, putting unordered classes at the end
    return classes.sort((a, b) => {
      const orderA = orderMap[a.id] !== undefined ? orderMap[a.id] : Number.MAX_SAFE_INTEGER;
      const orderB = orderMap[b.id] !== undefined ? orderMap[b.id] : Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  };

  const refreshSemesters = async() =>{
    const token = localStorage.getItem('jwtToken');

    try {
      const semestersResponse = await axios.get(`${apiUrl}/api/semesters`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      let sems = semestersResponse.data.map(sem => ({ ...sem, collapsed: (semestersResponse.data.length > 1 ? true: false) }));

      setSemesters(sems);
    } catch (semestersError) {
      console.error('Error fetching semesters:', semestersError);
    }
  };
  useEffect(() => {
    refreshSemesters();
  }, []);

  useEffect(() => {
    const pending = localStorage.getItem('onboarding_pending') === 'true';
    if (!pending) return;
    if (localStorage.getItem('onboarding_global_skipped') === 'true') return;
    if (localStorage.getItem('onboarding_semesters_shown') === 'true') return;
    if (semesters && semesters.length === 0) {
      setShowOnboarding(true);
    }
  }, [semesters]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowColorPicker(null);
      setShowFullColorPicker(null);
    };

    if (showColorPicker || showFullColorPicker) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showColorPicker, showFullColorPicker]);
  
useEffect(() => {
  if (isModalOpen || isClassModalOpen) {
    document.body.classList.add('modal-open');
  } else {
    document.body.classList.remove('modal-open');
  }
}, [isModalOpen, isClassModalOpen]);

const openClassModal = (semesterId) => {
  setSelectedSemesterId(semesterId);
  setIsClassModalOpen(true);
};

const closeClassModal = () => {
  setIsClassModalOpen(false);
  setNewClassName('');
  setSelectedSemesterId(null);
};

const handleCreateClass = async () => {
  const token = localStorage.getItem('jwtToken');
  const classData = {
    name: newClassName,
    semesters_id: selectedSemesterId,
  };

  try {
    await axios.post(`${apiUrl}/api/classes`, classData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    // Optionally, update the classes state to include the newly created class
    if (selectedSemesterId) {
      const response = await axios.get(
        `${apiUrl}/api/classes_by_semester_id/${selectedSemesterId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      // Find the semester to get its class_order
      const semester = semesters.find(s => s.id === selectedSemesterId);
      const sortedClasses = sortClassesByOrder(response.data, semester?.class_order);
      
      setClasses((prevClasses) => ({
        ...prevClasses,
        [selectedSemesterId]: sortedClasses,
      }));
    }
    closeClassModal();
  } catch (error) {
    console.error('Error creating class:', error);
    setError('Failed to create class. Please try again.');
  }
};
const toggleCollapse = (id) => {
  if (isModalOpen || isClassModalOpen) return; // Prevent toggling if a modal is open

  setSemesters((prevSemesters) =>
    prevSemesters.map((sem) =>
      sem.id === id ? { ...sem, collapsed: !sem.collapsed } : sem
    )
  );
};

useEffect(() => {
  const expandedSemester = semesters.find((sem) => !sem.collapsed && !classes[sem.id]);

  if (expandedSemester) {
    const fetchClasses = async () => {
      try {
        const token = localStorage.getItem('jwtToken');
        const response = await axios.get(
          `${apiUrl}/api/classes_by_semester_id/${expandedSemester.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        console.log(response.data);
        
        // Sort classes according to stored order if available
        const sortedClasses = sortClassesByOrder(response.data, expandedSemester.class_order);
        
        setClasses((prevClasses) => ({
          ...prevClasses,
          [expandedSemester.id]: sortedClasses,
        }));
      } catch (error) {
        console.error('Error fetching classes:', error);
        setError('Failed to fetch classes. Please try again.');
      }
    };

    fetchClasses();
  }
}, [semesters, classes]);


  const handleClassClick = (cls, clsList, sem) => {
    navigate('/class-details', { state: { currentClass: {...cls, semester_id: sem.id, semester_name: sem.name}, classes: clsList, email} });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setNewSemesterName('');
    setStartDate('');
    setEndDate('');
    setEditingSemesterId(null); // Reset editing state
  };

  const handleCreateSemester = async () => {
    const token = localStorage.getItem('jwtToken');
    const semesterData = {
      name: newSemesterName,
      start_date: startDate || null,
      end_date: endDate || null,
    };

    try {
      if (editingSemesterId) {
        // Update existing semester
        await axios.post(`${apiUrl}/api/semesters/${editingSemesterId}`, semesterData, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        // Update the semesters state
        setSemesters(prevSemesters =>
          prevSemesters.map(sem =>
            sem.id === editingSemesterId
              ? { ...sem, name: newSemesterName, start_date: startDate || null, end_date: endDate || null }
              : sem
          )
        );
      } else {
        // Create new semester
        await axios.post(`${apiUrl}/api/semesters`, semesterData, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        
        function timeout(delay) {
          return new Promise( res => setTimeout(res, delay) );
        }
        await timeout(300);
        
        // GET request to fetch the updated list of semesters
        const response = await axios.get(`${apiUrl}/api/semesters`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        // Update the semesters state with the newly fetched data
        console.log(response.data);
        let sems = response.data.map(sem => ({ ...sem, collapsed: (sem.name == newSemesterName ? false : true) }));
        setSemesters(sems);
      }
      
      closeModal();
    } catch (error) {
      console.error('Error saving semester:', error);
      setError('Failed to save semester. Please try again.');
    }
  };

  const handleEditSemester = (semester) => {
    setEditingSemesterId(semester.id);
    setNewSemesterName(semester.name);
    setStartDate(semester.start_date || '');
    setEndDate(semester.end_date || '');
    setIsModalOpen(true);
  };



  const handleEditClassName = async (classId, oldName, newName, sem_id) => {
    if (oldName == newName) {
      setEditingClassId(null);
      setEditedClassName('');
      setIsEditing(false);
      return;
    }
    const token = localStorage.getItem('jwtToken');
    try {
      await axios.post(`${apiUrl}/api/classes/${classId}`, { name: newName, semesters_id: sem_id }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      setClasses((prevClasses) => {
        const updatedClasses = { ...prevClasses };
        Object.keys(updatedClasses).forEach((semesterId) => {
          updatedClasses[semesterId] = updatedClasses[semesterId].map((cls) =>
            cls.id === classId ? { ...cls, name: newName } : cls
          );
        });
        return updatedClasses;
      });

      setEditingClassId(null);
      setEditedClassName('');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating class name:', error);
      setError('Failed to update class name. Please try again.');
    }
  };

  return (
    <div className="page-container">
      <Toolbar />
      <div className="content-wrap">
        <div className="semesters-container">
          {error && <p className="error-message">{error}</p>}
          {semesters.map((sem) => (
            <div
              key={sem.id}
              className={`semester-card ${sem.collapsed ? 'collapsed' : 'expanded'}`}
              onClick={() => toggleCollapse(sem.id)}
            >
              <div className="semester-header">
                <div style={{display:"flex", flexDirection: "column", justifyContent: "space-between"}}>
                  <span style={{fontWeight:"bold"}}>{sem.name}</span>
                  <span style={{fontSize:"14px"}}>
                    {sem.start_date && sem.end_date 
                      ? `${new Date(sem.start_date).toLocaleDateString()} - ${new Date(sem.end_date).toLocaleDateString()}`
                      : sem.start_date 
                        ? `From ${new Date(sem.start_date).toLocaleDateString()}`
                        : sem.end_date 
                          ? `Until ${new Date(sem.end_date).toLocaleDateString()}`
                          : 'No dates set'
                    }
                  </span>
                </div>
                
                <div style={{display: "flex", alignItems: "center", marginLeft: "20px"}}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditSemester(sem);
                    }}
                    style={{marginRight: "8px"}}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <DropdownIcon isOpen={!sem.collapsed} />
                </div>
              </div>
              {!sem.collapsed && (
                <>
                  {classes[sem.id] && (
                    <div className="classes-container">
                      {classes[sem.id].map((cls, index) => (
                        <div
                          key={cls.id}
                          className="class-card"
                          draggable={true}
                          onClick={() => {handleClassClick(cls, classes[sem.id], sem)}}
                          onDragStart={(e) => handleDragStart(e, cls, sem.id)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index, sem.id)}
                          style={{
                            display:"flex", 
                            flexDirection: "column", 
                            justifyContent: "space-between", 
                            position: "relative",
                            backgroundColor: getClassColor(cls.id),
                            color: 'white',
                            border: dragOverIndex === index ? '2px solid #ffeb3b' : 'none',
                            borderRadius: '5px',
                            overflow: 'hidden',
                            opacity: draggedClass && draggedClass.class.id === cls.id ? 0.5 : 1,
                            cursor: 'move'
                          }}
                        >
                          <div className="class-card-header">
                            {editingClassId === cls.id ? (
                              <textarea
                                ref={(el) => {
                                  if (el && editingClassId === cls.id) {
                                    el.focus();
                                    el.select(); // Select all text for easy editing
                                  }
                                }}
                                value={editedClassName}
                                onChange={(e) => {
                                  setEditedClassName(e.target.value);
                                  // Auto-resize textarea
                                  e.target.style.height = 'auto';
                                  e.target.style.height = e.target.scrollHeight + 'px';
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleEditClassName(cls.id, cls.name, editedClassName, sem.id);
                                  }
                                }}
                                style={{
                                  border: "none", 
                                  background: "rgba(255, 255, 255, 0.1)", // Light background to indicate editing
                                  outline: "none",
                                  color: "white",
                                  fontSize: "18px",
                                  fontWeight: "bold",
                                  maxWidth: "150px",
                                  position: "absolute",
                                  top: "45px", // Same as span
                                  left: "10px",
                                  fontFamily: "inherit",
                                  resize: "none",
                                  overflow: "hidden",
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                  lineHeight: "1.2",
                                  minHeight: "22px",
                                  padding: "2px 4px", // Small padding for the background
                                  borderRadius: "3px", // Subtle rounded corners
                                  boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.2)" // Subtle border
                                }}
                                className="class-name"
                                rows="1"
                              />
                            ) : (
                              <textarea
                                value={cls.name}
                                readOnly
                                style={{
                                  border: "none", 
                                  background: "transparent",
                                  outline: "none",
                                  color: "white",
                                  fontSize: "18px",
                                  fontWeight: "bold",
                                  maxWidth: "150px",
                                  position: "absolute",
                                  top: "43px", // Adjusted to align with edit mode (45px - 2px padding)
                                  left: "6px", // Adjusted to align with edit mode (10px - 4px padding)
                                  fontFamily: "inherit",
                                  resize: "none",
                                  overflow: "hidden",
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                  lineHeight: "1.2",
                                  minHeight: "22px",
                                  padding: "2px 4px", // Same padding as edit mode for consistent text position
                                  cursor: "default"
                                }}
                                className="class-name"
                                rows="1"
                              />
                            )}
                            <div style={{
                              position: "absolute",
                              top: "15px",
                              right: "15px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px"
                            }}>
                              <IconButton
                                className="color-picker-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowColorPicker(showColorPicker === cls.id ? null : cls.id);
                                }}
                                style={{
                                  backgroundColor: getClassColor(cls.id),
                                  width: "24px",
                                  height: "24px",
                                  minWidth: "24px",
                                  borderRadius: "50%",
                                  border: "2px solid white",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                  padding: 0
                                }}
                              >
                              </IconButton>
                              {editingClassId === cls.id ? (
                                <IconButton
                                  className="save-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClassName(cls.id, cls.name, editedClassName, sem.id);
                                  }}
                                  style={{
                                    backgroundColor: "rgba(255,255,255,0.2)",
                                    color: "white",
                                    width: "24px",
                                    height: "24px",
                                    minWidth: "24px",
                                    padding: 0,
                                    borderRadius: "4px",
                                    transition: "background-color 0.2s ease",
                                    border: "none",
                                    outline: "none",
                                    boxShadow: "none"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = "rgba(255,255,255,0.3)";
                                    e.target.style.boxShadow = "none";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = "rgba(255,255,255,0.2)";
                                    e.target.style.boxShadow = "none";
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.boxShadow = "none";
                                    e.target.style.outline = "none";
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.boxShadow = "none";
                                    e.target.style.outline = "none";
                                  }}
                                >
                                  <SaveIcon style={{ fontSize: "16px" }} />
                                </IconButton>
                              ) : (
                                <>
                                  <IconButton
                                    className="edit-icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingClassId(cls.id);
                                      setEditedClassName(cls.name);
                                      setIsEditing(true);
                                    }}
                                    style={{
                                      backgroundColor: "rgba(255,255,255,0.2)",
                                      color: "white",
                                      width: "24px",
                                      height: "24px",
                                      minWidth: "24px",
                                      padding: 0,
                                      borderRadius: "4px",
                                      transition: "background-color 0.2s ease",
                                      border: "none",
                                      outline: "none",
                                      boxShadow: "none"
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = "rgba(255,255,255,0.3)";
                                      e.target.style.boxShadow = "none";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = "rgba(255,255,255,0.2)";
                                      e.target.style.boxShadow = "none";
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.boxShadow = "none";
                                      e.target.style.outline = "none";
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.boxShadow = "none";
                                      e.target.style.outline = "none";
                                    }}
                                  >
                                    <EditIcon style={{ fontSize: "16px" }} />
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </div>
                          {/* Color Picker Dropdown */}
                          {showColorPicker === cls.id && (
                            <div 
                              className="color-picker-dropdown"
                              style={{
                                position: "sticky",
                                backgroundColor: "#4285F4",
                                borderRadius: "20px",
                                padding: "8px 12px",
                                display: "flex",
                                // alignItems: "center",
                                gap: "6px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {colorPalette.map((color, index) => (
                                <div
                                  key={index}
                                  onClick={() => handleColorSelect(cls.id, color)}
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    borderRadius: "50%",
                                    backgroundColor: color,
                                    cursor: "pointer",
                                    border: getClassColor(cls.id) === color ? "2px solid white" : "none",
                                    boxSizing: "border-box"
                                  }}
                                />
                              ))}
                              <span 
                                style={{ 
                                  color: "white", 
                                  fontSize: "12px", 
                                  marginLeft: "4px",
                                  cursor: "pointer"
                                }}
                                onClick={() => {
                                  setShowColorPicker(null);
                                  setShowFullColorPicker(cls.id);
                                }}
                              >
                                more ›
                              </span>
                            </div>
                          )}
                          
                          {/* Full Color Picker Modal */}
                          {showFullColorPicker === cls.id && (
                            <>
                              {/* Backdrop */}
                              <div 
                                style={{
                                  position: "fixed",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  backgroundColor: "rgba(0,0,0,0.5)",
                                  zIndex: 1500
                                }}
                                onClick={() => setShowFullColorPicker(null)}
                              />
                              {/* Modal */}
                              <div 
                                className="full-color-picker-modal"
                                style={{
                                  position: "fixed",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  backgroundColor: "white",
                                  borderRadius: "12px",
                                  padding: "20px",
                                  boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                                  zIndex: 2000,
                                  width: "320px"
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <h3 style={{ margin: "0 0 15px 0", color: "#333", fontSize: "16px" }}>
                                  Choose a color
                                </h3>
                                <div style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(8, 1fr)",
                                  gap: "8px",
                                  marginBottom: "15px"
                                }}>
                                  {extendedColorPalette.map((color, index) => (
                                    <div
                                      key={index}
                                      onClick={() => handleColorSelect(cls.id, color)}
                                      style={{
                                        width: "28px",
                                        height: "28px",
                                        borderRadius: "6px",
                                        backgroundColor: color,
                                        cursor: "pointer",
                                        border: getClassColor(cls.id) === color ? "3px solid #333" : "1px solid #e5e7eb",
                                        boxSizing: "border-box",
                                        transition: "transform 0.1s ease",
                                      }}
                                      onMouseEnter={(e) => e.target.style.transform = "scale(1.1)"}
                                      onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                                    />
                                  ))}
                                </div>
                                <div style={{ textAlign: "right" }}>
                                  <button
                                    onClick={() => setShowFullColorPicker(null)}
                                    style={{
                                      backgroundColor: "#f3f4f6",
                                      border: "none",
                                      borderRadius: "6px",
                                      padding: "8px 16px",
                                      cursor: "pointer",
                                      fontSize: "14px",
                                      color: "#374151"
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                          <div className="class-card-body">
                            <span className="enrollment-status">ENROLLED</span>
                            {cls.enrollment_count > 0 ?  <span className="no-students">{cls.enrollment_count} STUDENTS</span> : <span className="no-students">NO STUDENTS</span>}
                          </div>
                          
                        </div>
                      ))}
                      <div className="class-card-button">
                        <button
                          className="new-class-button"
                          onClick={(e) => { e.stopPropagation();  openClassModal(sem.id);}}
                        >
                          + New Class
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {isClassModalOpen && (
                <div className="modal-class-new">
                  <div className="modal-content-class">
                    <h2>Create New Class</h2>
                    <input
                      type="text"
                      value={newClassName}
                      onChange={(e) => setNewClassName(e.target.value)}
                      placeholder="Class Name"
                      className="class-input"
                    />
                    <button className="okButton" onClick={handleCreateClass}>Create</button>
                    <button className="cancelButton" onClick={closeClassModal}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {semesters.length === 0 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 'calc(100vh - 300px)', // Adjust this value based on your layout
              textAlign: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <p>Feel free to create a new semester!</p>
              <img 
                src='/assets/teacher-student-line-art.png' 
                alt="Teacher and student line art"
                style={{
                  opacity: 0.5,
                  maxWidth: '100%',
                  height: 'auto',
                  marginBottom: '20px'
                }}
              />
            </div>
          )}
          <div style={{display:"flex", justifyContent:"space-evenly", marginTop: "20px"}}>
          <button ref={newSemesterBtnRef} className="new-semester-button" onClick={openModal}>
            + New Semester
          </button>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="modal">
          <div className="modal-content-semester">
            <h2>{editingSemesterId ? 'Edit Semester' : 'Create New Semester'}</h2>
            <input
              type="text"
              value={newSemesterName}
              onChange={(e) => setNewSemesterName(e.target.value)}
              placeholder="Semester Name"
            />
            <div>
              <div className="date-selector">
                <label htmlFor="start-date">Start Date:</label>
                <input
                  type="date"
                  id="start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="date-input"
                />
              </div>
              <div className="date-selector">
                <label htmlFor="end-date">End Date:</label>
                <input
                  type="date"
                  id="end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-input"
                />
              </div>
            </div>
            <button onClick={handleCreateSemester}>{editingSemesterId ? 'Save' : 'Create'}</button>
            <button onClick={closeModal}>Cancel</button>
          </div>
        </div>
      )}

      <footer className="footer">
        <p>&copy; 2025 MyGradeMaster. All rights reserved.</p>
      </footer>
      {showOnboarding && (
        <OnboardingHint
          anchorRef={newSemesterBtnRef}
          title="Create your first semester"
          message="Start by creating a semester. Then you can add classes inside it."
          placement="top"
          ctaLabel="Okay"
          onClose={() => {
            setShowOnboarding(false);
            localStorage.setItem('onboarding_semesters_shown', 'true');
            localStorage.setItem('onboarding_pending', 'false');
          }}
          onSkipAll={() => {
            setShowOnboarding(false);
            localStorage.setItem('onboarding_global_skipped', 'true');
            localStorage.setItem('onboarding_pending', 'false');
          }}
        />
      )}
    </div>
  );
}

export default Semesters;
